import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const sheetOverlayVariants = cva(
  "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      variant: {
        glass: "bg-foreground/25",
        solid: "bg-foreground/80",
      },
    },
    defaultVariants: { variant: "glass" },
  },
);

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> &
    VariantProps<typeof sheetOverlayVariants>
>(({ className, variant, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(sheetOverlayVariants({ variant }), className)}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

/**
 * Sheet — variants :
 *  - `side` : top | right (défaut) | bottom | left.
 *  - `surface` : glass (défaut) | solid.
 *
 * Sur `side="bottom"`, ajoute automatiquement une poignée drag + safe-area.
 */
const sheetVariants = cva(
  "fixed z-50 gap-4 p-6 transition ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-fast data-[state=open]:duration-fast",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] rounded-t-3xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
      surface: {
        glass: "glass-strong shadow-cinematic border-foreground/8",
        solid: "bg-background shadow-lg border-border",
      },
    },
    compoundVariants: [
      { side: "top", surface: "glass", className: "border-b" },
      { side: "top", surface: "solid", className: "border-b" },
      { side: "bottom", surface: "glass", className: "border-t" },
      { side: "bottom", surface: "solid", className: "border-t" },
      { side: "left", surface: "glass", className: "border-r" },
      { side: "left", surface: "solid", className: "border-r" },
      { side: "right", surface: "glass", className: "border-l" },
      { side: "right", surface: "solid", className: "border-l" },
    ],
    defaultVariants: {
      side: "right",
      surface: "glass",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  overlayVariant?: VariantProps<typeof sheetOverlayVariants>["variant"];
  hideCloseButton?: boolean;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", surface = "glass", className, children, overlayVariant, hideCloseButton, ...props }, ref) => (
    <SheetPortal>
      <SheetOverlay variant={overlayVariant ?? (surface === "solid" ? "solid" : "glass")} />
      <SheetPrimitive.Content
        ref={ref}
        translate="no"
        className={cn(sheetVariants({ side, surface }), className)}
        {...props}
      >
        {/* Drag handle for bottom drawer */}
        {side === "bottom" && (
          <div
            className="mx-auto -mt-2 mb-2 h-1.5 w-12 rounded-full bg-foreground/15"
            aria-hidden="true"
          />
        )}
        {children}
        {!hideCloseButton && (
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-full opacity-70 ring-offset-background transition-[background-color,opacity,color] duration-fast hover:opacity-100 hover:bg-foreground/5 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none p-1.5">
            <X className="h-4 w-4" />
            <span className="sr-only">Fermer</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left -mx-6 -mt-6 px-6 pt-6 pb-4 mb-2 sticky top-0 z-10",
      "bg-gradient-to-b from-background/80 via-background/40 to-transparent backdrop-blur-sm",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-payfit-h2", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-payfit-body mt-1.5", className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  sheetVariants,
};
